import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, Check, X, Package, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

import {
  formatRupees,
  formatRupeesCompact,
  ORDER_STATUS_LABELS,
  UNIT_LABELS,
  type OrderStatus,
  type UnitOfMeasure,
} from '@sreerasa/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  usePendingProducts,
  useAllOrders,
  useModerateProduct,
  useUpdateOrderStatus,
} from '@/lib/queries';
import { useAuth } from '@/features/auth/auth-context';
import { supabase } from '@/lib/supabase';
import { cn, formatDateTime } from '@/lib/utils';
import { transitions } from '@/components/motion/primitives';
import { STATUS_VARIANT } from '../orders/index';

export const Route = createFileRoute('/admin/')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: '/auth/sign-in', search: { redirect: '/admin' } });
  },
  component: AdminConsole,
});

/**
 * The legal next steps from each status.
 *
 * This mirrors `guard_order_transition` (migration 0500) exactly. The trigger
 * is what actually enforces it — this table just stops the UI from offering a
 * button that would fail. If they ever disagree, the database wins.
 */
const NEXT_STATUS: Partial<Record<OrderStatus, OrderStatus[]>> = {
  pending_confirmation: ['confirmed', 'cancelled'],
  confirmed: ['packed', 'cancelled'],
  packed: ['out_for_delivery', 'cancelled'],
  out_for_delivery: ['delivered', 'cancelled'],
};

function AdminConsole() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState<'queue' | 'orders'>('queue');

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-lg px-4 py-28 text-center">
        <Shield className="mx-auto size-10 text-soil-300" aria-hidden />
        <h1 className="mt-4 font-display text-2xl font-bold text-soil-900">Not for you</h1>
        <p className="mt-2 text-soil-600">This console is for SreeRasa staff.</p>
        <Link to="/" className="mt-6 inline-block">
          <Button>Back home</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex items-center gap-3">
        <div className="flex size-10 items-center justify-center rounded-xl bg-soil-900 text-white">
          <Shield className="size-5" aria-hidden />
        </div>
        <div>
          <h1 className="font-display text-3xl font-black text-soil-900">Admin</h1>
          <p className="text-sm text-soil-500">Moderate listings, move orders.</p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="mt-8 flex gap-1 rounded-xl bg-soil-100 p-1">
        {(
          [
            { value: 'queue', label: 'Review queue' },
            { value: 'orders', label: 'Orders' },
          ] as const
        ).map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setTab(option.value)}
            className={cn(
              'relative flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              tab === option.value ? 'text-soil-900' : 'text-soil-500 hover:text-soil-700',
            )}
          >
            {tab === option.value && (
              <motion.span
                layoutId="admin-tab"
                className="absolute inset-0 rounded-lg bg-white shadow-soft"
                transition={transitions.spring}
              />
            )}
            <span className="relative">{option.label}</span>
          </button>
        ))}
      </div>

      <div className="mt-6">{tab === 'queue' ? <ReviewQueue /> : <OrdersBoard />}</div>
    </div>
  );
}

/* ── Review queue ─────────────────────────────────────────────────────── */

function ReviewQueue() {
  const { data: products, isLoading } = usePendingProducts();
  const moderate = useModerateProduct();
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  async function handleApprove(id: string, title: string) {
    try {
      await moderate.mutateAsync({ id, approve: true });
      toast.success(`${title} is live`);
    } catch (error) {
      toast.error('Could not approve', {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }

  async function handleReject(id: string, title: string) {
    if (!reason.trim()) {
      toast.error('Give a reason — the farmer sees it');
      return;
    }
    try {
      await moderate.mutateAsync({ id, approve: false, reason });
      toast.success(`${title} rejected`);
      setRejecting(null);
      setReason('');
    } catch (error) {
      toast.error('Could not reject', {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }, (_, i) => (
          <Skeleton key={i} className="h-32 rounded-card" />
        ))}
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="rounded-card border border-dashed border-soil-300 bg-white/50 p-16 text-center">
        <Check className="mx-auto size-9 text-farm-500" aria-hidden />
        <h2 className="mt-4 font-display text-xl font-bold text-soil-800">Queue is clear</h2>
        <p className="mt-2 text-sm text-soil-500">Nothing waiting on review.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-soil-500">
        {products.length} {products.length === 1 ? 'listing' : 'listings'} waiting. Nothing goes
        live until someone here says so.
      </p>

      <AnimatePresence mode="popLayout">
        {products.map((product) => {
          const farm = product.farms as unknown as {
            name: string;
            village: string;
            district: string;
          } | null;
          const farmer = product.profiles as unknown as { display_name: string } | null;

          return (
            <motion.div
              key={product.id}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={transitions.base}
              className="rounded-card border border-soil-200 bg-white p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display font-bold text-soil-900">{product.title}</h3>
                    <Badge variant="warning" size="sm">
                      <Clock aria-hidden /> Pending
                    </Badge>
                  </div>

                  {farm && (
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-soil-500">
                      <MapPin className="size-3.5" aria-hidden />
                      {farm.name} · {farm.village}, {farm.district}
                      {farmer && ` · ${farmer.display_name}`}
                    </p>
                  )}

                  {product.description && (
                    <p className="mt-2 line-clamp-2 text-sm text-soil-600">{product.description}</p>
                  )}

                  <p className="mt-2 text-sm font-medium text-soil-900">
                    {formatRupeesCompact(product.price_paise)} per{' '}
                    {UNIT_LABELS[product.unit as UnitOfMeasure] ?? product.unit} ·{' '}
                    {product.stock_qty} in stock
                  </p>

                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {product.is_naturally_ripened && (
                      <Badge variant="field" size="sm">
                        Naturally ripened
                      </Badge>
                    )}
                    {product.is_chemical_free && (
                      <Badge variant="farm" size="sm">
                        No chemicals
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleApprove(product.id, product.title)}
                    isLoading={moderate.isPending}
                  >
                    <Check aria-hidden /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRejecting(rejecting === product.id ? null : product.id)}
                  >
                    <X aria-hidden /> Reject
                  </Button>
                </div>
              </div>

              <AnimatePresence>
                {rejecting === product.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={transitions.quick}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 space-y-3 border-t border-soil-100 pt-4">
                      <textarea
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder="Why is this being rejected? The farmer sees this text."
                        rows={2}
                        className="w-full rounded-xl border border-soil-200 p-3 text-sm focus:border-farm-500 focus:outline-none focus:ring-4 focus:ring-farm-500/12"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleReject(product.id, product.title)}
                          isLoading={moderate.isPending}
                        >
                          Confirm rejection
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRejecting(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

/* ── Orders board ─────────────────────────────────────────────────────── */

function OrdersBoard() {
  const { data: orders, isLoading } = useAllOrders();
  const updateStatus = useUpdateOrderStatus();

  async function handleAdvance(id: string, status: OrderStatus, number: string) {
    try {
      await updateStatus.mutateAsync({ id, status });
      toast.success(`${number} → ${ORDER_STATUS_LABELS[status]}`);
    } catch (error) {
      // The transition trigger raises on an illegal move. If this fires, the
      // NEXT_STATUS table above has drifted from the migration.
      toast.error('Could not update', {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-28 rounded-card" />
        ))}
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="rounded-card border border-dashed border-soil-300 bg-white/50 p-16 text-center">
        <Package className="mx-auto size-9 text-soil-300" aria-hidden />
        <h2 className="mt-4 font-display text-xl font-bold text-soil-800">No orders yet</h2>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {orders.map((order) => {
        const next = NEXT_STATUS[order.status] ?? [];

        return (
          <div key={order.id} className="rounded-card border border-soil-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/orders/$id"
                    params={{ id: order.id }}
                    className="font-display font-bold text-soil-900 hover:text-farm-700"
                  >
                    {order.order_number}
                  </Link>
                  <Badge variant={STATUS_VARIANT[order.status]} size="sm">
                    {ORDER_STATUS_LABELS[order.status]}
                  </Badge>
                  <Badge variant="neutral" size="sm">
                    {order.payment_method.toUpperCase()}
                  </Badge>
                </div>

                <p className="mt-1 text-sm text-soil-500">
                  {formatDateTime(order.placed_at)} · {order.delivery_name} · {order.delivery_phone}
                </p>
                <p className="mt-1 text-sm text-soil-600">
                  {order.delivery_city}, {order.delivery_pincode}
                  {order.delivery_slot && ` · ${order.delivery_slot}`}
                </p>
                <p className="mt-1 text-xs text-soil-500">
                  {order.order_items
                    ?.map((item) => `${item.quantity}${item.unit_snapshot} ${item.title_snapshot}`)
                    .join(' · ')}
                </p>
              </div>

              <div className="text-right">
                <p className="font-display text-xl font-bold text-soil-900">
                  {formatRupees(order.total_paise)}
                </p>
              </div>
            </div>

            {next.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2 border-t border-soil-100 pt-4">
                {next.map((status) => (
                  <Button
                    key={status}
                    size="sm"
                    variant={status === 'cancelled' ? 'outline' : 'primary'}
                    onClick={() => handleAdvance(order.id, status, order.order_number)}
                    isLoading={updateStatus.isPending}
                  >
                    Mark {ORDER_STATUS_LABELS[status].toLowerCase()}
                  </Button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
